import { COMMERCE_EVENT_NAMES } from '../eo_mapping';
import * as CALCULATION_KEY from '../eo_mapping/calculations';
import { ColumnMetadataTypes, ColumnMetadata, ColumnValueFormula, Popover, Group } from './eo_types';
import { FORMATTER } from '../../utils';
import { isActiveFlipper } from '../../../main/routers/RouterServices';
import {
  TRUEVIEW_ANALYTICS_CALCULATION_COLUMNS,
  TRUEVIEW_ANALYTICS_SKAN_COLUMNS,
  TRUEVIEW_ANALYTICS_TOTALS_COLUMNS,
} from '../../../main/screens/ScreenAdsTrueView/trueViewAnalytics.constants';
import { OTT_ANALYTICS_CALCULATION_COLUMNS } from '../../../main/screens/ScreenOtt/ott.constants';
import { DATA_FIELD_KEYS } from '../../../../../shared/constants/dataFieldKeys';
import { convertSKANPostbackIndexToHumanReadable } from './druid/convertSKANPostbackIndexToHumanReadable';
import { POSTBACK_SEQUENCE_INDEX_LABEL } from '../../../main/screens/ScreenSKAdNetwork/ScreenSKAdNetworkConstants';
import {
  ECPA,
  ECPA_IN_LOCAL_CURRENCY,
  ECPC,
  ECPC_IN_LOCAL_CURRENCY,
  ECPI,
  ECPI_IN_LOCAL_CURRENCY,
  ECPM,
  ECPM_IN_LOCAL_CURRENCY,
  ROAS,
  ROI,
} from '../eo_mapping/datasources';

export const UNDISCLOSED_EVENT_LABEL = 'undisclosed';
export const UNDISCLOSED_EVENT_VALUE = null;

const journeyCreativesTabEnabled = isActiveFlipper('journey_creatives_tab');

const cleanNumber = (v: string | number | null): number | null => {
  if (v === UNDISCLOSED_EVENT_VALUE) {
    return UNDISCLOSED_EVENT_VALUE;
  }

  const numericVal = Number(v);

  return isNaN(numericVal) ? 0 : numericVal;
};

const cleanPositiveNumber = (positiveNumber: string | number | null): number | null => {
  if (positiveNumber === UNDISCLOSED_EVENT_VALUE) {
    return UNDISCLOSED_EVENT_VALUE;
  }

  const cleanedPositiveNumber = cleanNumber(positiveNumber) as number;

  return cleanedPositiveNumber >= 0 ? cleanedPositiveNumber : 0;
};

export const CUSTOM_EVENT_PREFIX = 'eo_custom_event-';

export const SERVER_CUSTOM_EVENT_PREFIX = 'custom_event__';

export const BLOCKED_CUSTOM_EVENT_PREFIX = 'eo_custom_event_blocked-';

export const METADATA_TYPE: ColumnMetadataTypes = {
  dimension: {
    formatter: (v) => v,
    cleanValue: (v) => v || 'Unpopulated',
  },
  data_sources: {
    formatter: FORMATTER.basic,
    cleanValue: cleanNumber,
  },
  eo_custom_event: {
    formatter: FORMATTER.basic,
    cleanValue: cleanNumber,
  },
  skan: {
    formatter: FORMATTER.basic,
    cleanValue: cleanNumber,
  },
  eo_custom_event_blocked: {
    formatter: FORMATTER.basic,
    cleanValue: cleanNumber,
  },
  eo_commerce_event: {
    formatter: FORMATTER.basic,
    cleanValue: cleanNumber,
  },
  formula: {
    formatter: FORMATTER.percentage,
    cleanValue: cleanPositiveNumber,
  },
  percentage: {
    formatter: FORMATTER.decimal,
    cleanValue: cleanPositiveNumber,
  },
  currency_formula: {
    formatter: FORMATTER.currency,
    cleanValue: cleanPositiveNumber,
  },
  hours_from: {
    formatter: FORMATTER.basic,
  },
  standard_event: {
    formatter: FORMATTER.basic,
    cleanValue: cleanNumber,
  },
  custom_calculation: {
    cleanValue: cleanPositiveNumber,
  },
};

export const COLUMN_METADATA: { [column: string]: ColumnMetadata } = {
  revenue: METADATA_TYPE.currency_formula,
  revenue_in_local_currency: METADATA_TYPE.currency_formula,
  revenue_blocked: METADATA_TYPE.currency_formula,
  cost: METADATA_TYPE.currency_formula,
  cost_in_app_local_currency: METADATA_TYPE.currency_formula,
  calculated_eCPI: METADATA_TYPE.currency_formula,
  calculated_eCPC: METADATA_TYPE.currency_formula,
  calculated_eCPM: METADATA_TYPE.currency_formula,
  calculated_eCPA: METADATA_TYPE.currency_formula,
  calculated_eCPA_in_local_currency: METADATA_TYPE.currency_formula,
  calculated_eCPC_in_local_currency: METADATA_TYPE.currency_formula,
  calculated_eCPI_in_local_currency: METADATA_TYPE.currency_formula,
  calculated_eCPM_in_local_currency: METADATA_TYPE.currency_formula,
  calculated_ROI: {
    formatter: (v) => {
      return v === UNDISCLOSED_EVENT_VALUE ? UNDISCLOSED_EVENT_VALUE : `${parseFloat(String(v)).toFixed(2)}%`;
    },
    cleanValue: cleanPositiveNumber,
  },
  calculated_ROAS: {
    formatter: (v) => {
      return v === UNDISCLOSED_EVENT_VALUE ? UNDISCLOSED_EVENT_VALUE : `${parseFloat(String(v)).toFixed(2)}%`;
    },
    cleanValue: cleanPositiveNumber,
  },
  eo_click: {
    style: { textAlign: 'right' },
  },
  eo_impression: {
    style: { textAlign: 'right' },
  },
  last_attributed_touch_data_tilde_tags: {
    style: { textAlign: 'left' },
  },

  [POSTBACK_SEQUENCE_INDEX_LABEL]: {
    formatter: (v) => convertSKANPostbackIndexToHumanReadable(String(v)),
  },

  date_range: {
    cleanValue: (v) => v || '',
  },

  attributed: {
    formatter: (v) => (v === 'false' ? 'false (organic)' : v),
  },

  [TRUEVIEW_ANALYTICS_CALCULATION_COLUMNS.CLICK_TO_TOTAL_INSTALL]: METADATA_TYPE.formula,
  [TRUEVIEW_ANALYTICS_CALCULATION_COLUMNS.CLICK_TO_TOTAL_PURCHASE]: METADATA_TYPE.formula,
  [TRUEVIEW_ANALYTICS_CALCULATION_COLUMNS.TOTAL_INSTALL_TO_COST]: METADATA_TYPE.currency_formula,
  [TRUEVIEW_ANALYTICS_CALCULATION_COLUMNS.TOTAL_PURCHASE_TO_COST]: METADATA_TYPE.currency_formula,
  [TRUEVIEW_ANALYTICS_SKAN_COLUMNS.REVENUE]: METADATA_TYPE.currency_formula,
  [TRUEVIEW_ANALYTICS_TOTALS_COLUMNS.REVENUE]: METADATA_TYPE.currency_formula,

  [OTT_ANALYTICS_CALCULATION_COLUMNS.IMPRESSION_CONVERSION_RATE]: METADATA_TYPE.formula,
};

export const FORMULAS: { [formulaName: string]: ColumnValueFormula[] } = {
  fraud_codes: [
    {
      fn: 'concat',
      fields: ['fraud_codes'],
      type: 'dimension_derived',
      key: 'fraud_codes_description',
    },
  ],
  eo_install: [
    {
      fn: '/',
      fields: ['eo_install', 'eo_click'],
      key: 'eo_click_to_eo_install',
    },
  ],
  eo_reinstall: [
    {
      fn: '/',
      fields: ['eo_reinstall', 'eo_click'],
      key: 'eo_click_to_eo_reinstall',
    },
  ],
  eo_open: [{ fn: '/', fields: ['eo_open', 'eo_click'], key: 'eo_click_to_eo_open' }],
  eo_web_session_start: [
    {
      fn: '/',
      fields: ['eo_web_session_start', 'eo_click'],
      key: 'eo_click_to_eo_web_session_start',
    },
  ],
  eo_branch_cta_view: [
    {
      fn: '/',
      fields: ['eo_click', 'eo_branch_cta_view'],
      key: 'eo_branch_cta_view_to_eo_click',
    },
    {
      fn: '/',
      fields: ['eo_install', 'eo_branch_cta_view'],
      key: 'eo_branch_cta_view_to_eo_install',
    },
    {
      fn: '/',
      fields: ['eo_open', 'eo_branch_cta_view'],
      key: 'eo_branch_cta_view_to_eo_open',
    },
    {
      fn: '/',
      fields: ['eo_dismissal', 'eo_branch_cta_view'],
      key: 'eo_branch_cta_view_to_eo_dismissal',
    },
  ].concat(
    Object.keys(COMMERCE_EVENT_NAMES)
      .filter((e) => e !== 'revenue' && e !== 'revenue_in_local_currency')
      .map((e) => {
        return {
          fn: '/',
          fields: [e, 'eo_branch_cta_view'],
          key: `eo_branch_cta_view_to_${e}`,
        };
      })
  ),
  eo_install_blocked: [
    {
      key: 'eo_install_blocked_to_eo_install_total',
      fn: '/',
      fields: ['eo_install_blocked', { fn: '+', fields: ['eo_install', 'eo_install_blocked'] }],
    },
    {
      key: 'eo_install_plus_blocked',
      type: 'eo_custom_event_blocked',
      fn: '+',
      fields: ['eo_install', 'eo_install_blocked'],
    },
  ],
  eo_click_blocked: [
    {
      key: 'eo_click_blocked_to_eo_click_total',
      fn: '/',
      fields: [
        'eo_click_blocked',
        {
          fn: '+',
          fields: ['eo_click', 'eo_click_blocked'],
        },
      ],
    },
    {
      key: 'eo_click_plus_blocked',
      type: 'eo_custom_event_blocked',
      fn: '+',
      fields: ['eo_click', 'eo_click_blocked'],
    },
  ],
  eo_open_blocked: [
    {
      key: 'eo_open_blocked_to_eo_open_total',
      fn: '/',
      fields: [
        'eo_open_blocked',
        {
          fn: '+',
          fields: ['eo_open', 'eo_open_blocked'],
        },
      ],
    },
    {
      key: 'eo_open_plus_blocked',
      type: 'eo_custom_event_blocked',
      fn: '+',
      fields: ['eo_open', 'eo_open_blocked'],
    },
  ],
  eo_reinstall_blocked: [
    {
      key: 'eo_reinstall_blocked_to_eo_reinstall_total',
      fn: '/',
      fields: ['eo_reinstall_blocked', { fn: '+', fields: ['eo_reinstall', 'eo_reinstall_blocked'] }],
    },
    {
      key: 'eo_reinstall_plus_blocked',
      type: 'eo_custom_event_blocked',
      fn: '+',
      fields: ['eo_reinstall', 'eo_reinstall_blocked'],
    },
  ],
  eo_impression_blocked: [
    {
      key: 'eo_impression_blocked_to_eo_impression_total',
      fn: '/',
      fields: ['eo_impression_blocked', { fn: '+', fields: ['eo_impression', 'eo_impression_blocked'] }],
    },
    {
      key: 'eo_impression_plus_blocked',
      type: 'eo_custom_event_blocked',
      fn: '+',
      fields: ['eo_impression', 'eo_impression_blocked'],
    },
  ],
};

export const POPOVERS: { [key: string]: Popover } = {
  cost: {
    title: 'Total cost (spend) for those dimensions (analytics tags, user data, time range), regardless of cost model',
  },
  calculated_eCPI: {
    title: 'Effective Cost Per Install: cost divided by number of installs',
  },
  calculated_eCPC: {
    title: 'Effective Cost Per Click: cost divided by number of clicks',
  },
  calculated_eCPM: {
    title: 'Effective Cost Per Milli: cost divided by impressions divided by 1000',
  },
  calculated_eCPA: {
    title: 'Effective Cost Per Action: cost divided by purchases [includes web and app purchases]',
  },
  cost_in_app_local_currency: {
    title:
      'Total cost in local currency (spend) for those dimensions (analytics tags, user data, time range), regardless of cost model',
  },
  calculated_eCPI_in_local_currency: {
    title: 'Effective Cost Per Install: cost in local currency divided by number of installs',
  },
  calculated_eCPC_in_local_currency: {
    title: 'Effective Cost Per Click: cost in local currency divided by number of clicks',
  },
  calculated_eCPM_in_local_currency: {
    title: 'Effective Cost Per Milli: cost in local currency divided by impressions divided by 1000',
  },
  calculated_eCPA_in_local_currency: {
    title: 'Effective Cost Per Action: cost in local currency divided by purchases [includes web and app purchases]',
  },
  calculated_ROI: {
    title: 'Return On Investment: profit (revenue minus cost) divided by cost, as a percentage',
  },
  calculated_ROAS: {
    title: 'Return On Ad Spend: revenue divided by cost, as a percentage',
  },
  eo_install_hours_from_last_attributed_touch_to_event: {
    title:
      'This shows the average number of hours from a click occurring to an install being recorded. This should be used for fraud detection only - measuring which secondary publishers have an irregularly high average time for click to install. This metric is capped at 24 hours per event, making it useful primarily for fraud detection.',
  },
  eo_reinstall_hours_from_last_attributed_touch_to_event: {
    title:
      'This shows the average number of hours from a click occurring to a reinstall being recorded. This should be used for fraud detection only - measuring which secondary publishers have an irregularly high average time for click to install. This metric is capped at 24 hours per event, making it useful primarily for fraud detection.',
  },
  eo_install_plus_blocked: {
    title: 'Blocked and unblocked installs',
  },
  eo_install_blocked: {
    title: 'Installs blocked based on active fraud rules',
  },
  eo_install_blocked_to_eo_install_total: {
    title: 'Percentage of blocked installs over total installs',
  },
  eo_click_plus_blocked: {
    title: 'Blocked and unblocked clicks',
  },
  eo_click_blocked: {
    title: 'Clicks blocked based on active fraud rules',
  },
  eo_click_blocked_to_eo_click_total: {
    title: 'Percentage of blocked clicks over total clicks',
  },
  eo_open_plus_blocked: {
    title: 'Blocked and unblocked opens',
  },
  eo_open_blocked: {
    title: 'Opens blocked based on active fraud rules',
  },
  eo_open_blocked_to_eo_open_total: {
    title: 'Percentage of blocked opens over total opens',
  },
  eo_reinstall_plus_blocked: {
    title: 'Blocked and unblocked reinstalls',
  },
  eo_reinstall_blocked: {
    title: 'Reinstalls blocked based on active fraud rules',
  },
  eo_reinstall_blocked_to_eo_reinstall_total: {
    title: 'Percentage of blocked reinstalls over total reinstalls',
  },
  eo_impression_plus_blocked: {
    title: 'Blocked and unblocked impressions',
  },
  eo_impression_blocked: {
    title: 'Impressions blocked based on active fraud rules',
  },
  eo_impression_blocked_to_eo_impression_total: {
    title: 'Percentage of blocked impressions over total impressions',
  },
  eo_install_seconds_from_last_attributed_touch_to_event_under_10: {
    title:
      'The percentage of unblocked installs where the time from click to install was under 10 seconds - an indicator of click injection',
  },
  eo_install_seconds_from_last_attributed_touch_to_event_between_10_and_30: {
    title: 'The percentage of unblocked installs where the time from click to install was between 10 and 30 seconds',
  },
  eo_install_seconds_from_last_attributed_touch_to_event_over_30: {
    title: 'The percentage of unblocked installs where the time from click to install was over 30 seconds',
  },
  eo_install_days_from_last_attributed_touch_to_event_under_1: {
    title: 'The percentage of unblocked installs where the time from click to install was under 1 day',
  },
  eo_install_days_from_last_attributed_touch_to_event_over_1: {
    title:
      'The percentage of unblocked installs where the time from click to install was over 1 day - an indicator of click flooding',
  },
  eo_click_to_eo_install: {
    title:
      'The percentage of attributed installs divided by clicks. Installs with "last attributed touch type" of value "IMPRESSION" are filtered out. Installs with "platform" of value "DESKTOP_WEB" and "DESKTOP_APP" are also filtered out',
  },
  eo_click_to_eo_reinstall: {
    title: 'The percentage of attributed reinstalls divided by clicks',
  },
  eo_click_to_eo_open: {
    title:
      'The percentage of attributed opens divided by clicks. Opens with "last attributed touch type" of value "IMPRESSION" are filtered out. Opens with "platform" of value "DESKTOP_WEB" and "DESKTOP_APP" are also filtered out',
  },
  eo_click_to_eo_web_session_start: {
    title: 'The percentage of attributed web session starts divided by clicks',
  },
  eo_branch_cta_view_to_eo_click: {
    title: 'The percentage of clicks attributed to Journeys divided by Journeys views',
  },
  eo_branch_cta_view_to_eo_install: {
    title: 'The percentage of installs attributed to Journeys divided by Journeys views',
  },
  eo_branch_cta_view_to_eo_open: {
    title: 'The percentage of opens attributed to Journeys divided by Journeys views',
  },
  eo_branch_cta_view_to_eo_dismissal: {
    title: 'The percentage of dismissals attributed to Journey divided by Journeys views',
  },
  eo_branch_cta_view_to_PURCHASE: {
    title: 'The percentage of purchase events attributed to Journeys, divided by Journeys views',
  },
  eo_branch_cta_view_to_ADD_TO_CART: {
    title: 'The percentage of add to cart events attributed to Journeys, divided by Journeys views',
  },
  eo_branch_cta_view_to_ADD_TO_WISHLIST: {
    title: 'The percentage of add to wishlist events attributed to Journeys, divided by Journeys views',
  },
  eo_branch_cta_view_to_VIEW_CART: {
    title: 'The percentage of view cart events attributed to Journeys, divided by Journeys views',
  },
  eo_branch_cta_view_to_INITIATE_PURCHASE: {
    title: 'The percentage of initiate purchase events attributed to Journeys, divided by Journeys views',
  },
  eo_branch_cta_view_to_ADD_PAYMENT_INFO: {
    title: 'The percentage of add payment info events attributed to Journeys, divided by Journeys views',
  },
  eo_branch_cta_view_to_SPEND_CREDITS: {
    title: 'The percentage of spend credit events attributed to Journeys, divided by Journeys views',
  },
  last_attributed_touch_data_tilde_journey_name: {
    title: "'Campaign Name' or 'title' for an audience rule / Journeys banner",
  },
  last_attributed_touch_data_tilde_journey_id: {
    title: 'Audience rule id for a Journeys banner',
  },
  last_attributed_touch_data_tilde_view_name: {
    title: journeyCreativesTabEnabled
      ? "'Creative Name' or 'title' for a Journey creative / template"
      : "'View Name' or 'title' for a Branch view / template",
  },
  last_attributed_touch_data_tilde_view_id: {
    title: journeyCreativesTabEnabled ? 'Creative ID for a Journey creative' : 'Branch view id for a Journeys banner',
  },
  last_attributed_touch_data_tilde_agency_id: {
    title: 'Agency id',
  },
  last_attributed_touch_data_tilde_agency: {
    title: 'Agency name',
  },
  eo_click: {
    title:
      'Number of times users tapped on a Branch link, or on an ad where Branch is able to fetch click data from a third party (e.g. Facebook ads)',
  },
  eo_install: {
    title: 'Installs attributed to this feature – installs are the first app open after downloading the app',
  },
  eo_reinstall: {
    title: 'Reinstalls attributed to your selected filters',
  },
  eo_open: {
    title: 'App opens attributed to your selected filters',
  },
  eo_web_session_start: {
    title:
      'Web sessions attributed to your selected filters – triggered when a new tab for a website is opened, or when a Branch link is clicked that leads to the website',
  },
  eo_branch_cta_view: {
    title: 'Views of a Journeys banner or interstitial',
  },
  eo_sms_sent: {
    title: 'Number of times users texted themselves an app download link from desktop web',
  },
  eo_web_to_app_auto_redirect: {
    title: 'Number of times users were auto-redirected to the app without clicking, upon landing on a web page',
  },
  eo_impression: {
    title: 'Ad impressions',
  },
  eo_pageview: {
    title: 'Attributed pageviews',
  },
  eo_dismissal: {
    title: 'Journey Dismissals',
  },
  last_attributed_touch_data_tilde_feature: {
    title:
      'Feature of last touch – this can be set as a custom analytics tag or can be prefilled with the Branch feature, e.g. Journeys',
  },
  last_attributed_touch_data_tilde_channel: {
    title: 'Channel of last touch – this can be set as a custom analytics tag',
  },
  last_attributed_touch_data_tilde_organic_search_url: {
    title: 'Organic Search URL',
  },
  last_attributed_touch_data_tilde_campaign: {
    title:
      'Campaign of last touch – this can be set as a custom analytics tag, or set automatically by ad networks like Facebook',
  },
  last_attributed_touch_data_tilde_stage: {
    title: 'Stage of last touch – this can be set as a custom analytics tag',
  },
  last_attributed_touch_data_tilde_tags: {
    title: 'Tags of last touch – this can be set as a custom analytics tag',
  },
  PURCHASE: {
    title: 'Purchases attributed to your selected filters',
  },
  revenue: {
    title: 'Revenue attributed to your selected filters',
  },

  ADD_TO_CART: {
    title: 'Add to cart events attributed to your selected filters',
  },
  ADD_TO_WISHLIST: {
    title: 'Add to wishlist events attributed to your selected filters',
  },
  VIEW_CART: {
    title: 'View cart events attributed to your selected filters',
  },
  INITIATE_PURCHASE: {
    title: 'Initiate purchase events attributed to your selected filters',
  },
  ADD_PAYMENT_INFO: {
    title: 'Add payment info events attributed to your selected filters',
  },
  SPEND_CREDITS: {
    title: 'Spend credits events attributed to your selected filters',
  },
  RESERVE: {
    title: 'Reserve events attributed to your selected filters',
  },
  SEARCH: {
    title: 'Search events attributed to your selected filters',
  },
  CLICK_AD: {
    title: 'Click ad events attributed to your selected filters',
  },
  VIEW_AD: {
    title: 'View ad events attributed to your selected filters',
  },
  VIEW_ITEM: {
    title:
      'View item events attributed to your selected filters – usually on a screen that can only support showing one item',
  },
  VIEW_ITEMS: {
    title:
      'View items events attributed to your selected filters – usually on a screen that can support showing multiple items, such as a list view',
  },
  RATE: {
    title: 'Rate events attributed to your selected filters',
  },
  SHARE: {
    title: 'Share events attributed to your selected filters',
  },
  INITIATE_STREAM: {
    title: 'Initiate stream events attributed to your selected filters',
  },
  COMPLETE_STREAM: {
    title: 'Complete stream events attributed to your selected filters',
  },
  COMPLETE_REGISTRATION: {
    title: 'Complete registration events attributed to your selected filters',
  },
  COMPLETE_TUTORIAL: {
    title: 'Complete tutorial events attributed to your selected filters',
  },
  ACHIEVE_LEVEL: {
    title: 'Achieve level events attributed to your selected filters',
  },
  UNLOCK_ACHIEVEMENT: {
    title: 'Unlock achievement events attributed to your selected filters',
  },
  INVITE: {
    title: 'Invite events attributed to your selected filters',
  },
  LOGIN: {
    title: 'Login events attributed to your selected filters',
  },
  SUBSCRIBE: {
    title: 'Subscribe events attributed to your selected filters',
  },
  START_TRIAL: {
    title: 'Start trial events attributed to your selected filters',
  },

  PURCHASE_BLOCKED: {
    title: 'Blocked purchases attributed to your selected filters',
  },
  revenue_blocked: {
    title: 'Blocked revenue attributed to your selected filters',
  },

  ADD_TO_CART_BLOCKED: {
    title: 'Blocked add to cart events attributed to your selected filters',
  },
  ADD_TO_WISHLIST_BLOCKED: {
    title: 'Blocked add to wishlist events attributed to your selected filters',
  },
  VIEW_CART_BLOCKED: {
    title: 'Blocked view cart events attributed to your selected filters',
  },
  INITIATE_PURCHASE_BLOCKED: {
    title: 'Blocked initiate purchase events attributed to your selected filters',
  },
  ADD_PAYMENT_INFO_BLOCKED: {
    title: 'Blocked add payment info events attributed to your selected filters',
  },
  SPEND_CREDITS_BLOCKED: {
    title: 'Blocked spend credits events attributed to your selected filters',
  },
  RESERVE_BLOCKED: {
    title: 'Blocked reserve events attributed to your selected filters',
  },
  SEARCH_BLOCKED: {
    title: 'Blocked search events attributed to your selected filters',
  },
  CLICK_AD_BLOCKED: {
    title: 'Blocked click ad events attributed to your selected filters',
  },
  VIEW_AD_BLOCKED: {
    title: 'Blocked view ad events attributed to your selected filters',
  },
  VIEW_ITEM_BLOCKED: {
    title:
      'Blocked view item events attributed to your selected filters – usually on a screen that can only support showing one item',
  },
  VIEW_ITEMS_BLOCKED: {
    title:
      'Blocked view items events attributed to your selected filters – usually on a screen that can support showing multiple items, such as a list view',
  },
  RATE_BLOCKED: {
    title: 'Blocked rate events attributed to your selected filters',
  },
  SHARE_BLOCKED: {
    title: 'Blocked share events attributed to your selected filters',
  },
  COMPLETE_REGISTRATION_BLOCKED: {
    title: 'Blocked complete registration events attributed to your selected filters',
  },
  COMPLETE_TUTORIAL_BLOCKED: {
    title: 'Blocked complete tutorial events attributed to your selected filters',
  },
  ACHIEVE_LEVEL_BLOCKED: {
    title: 'Blocked achieve level events attributed to your selected filters',
  },
  UNLOCK_ACHIEVEMENT_BLOCKED: {
    title: 'Blocked unlock achievement events attributed to your selected filters',
  },
  INVITE_BLOCKED: {
    title: 'Blocked invite events attributed to your selected filters',
  },
  LOGIN_BLOCKED: {
    title: 'Blocked login events attributed to your selected filters',
  },
  SUBSCRIBE_BLOCKED: {
    title: 'Blocked subscribe events attributed to your selected filters',
  },
  START_TRIAL_BLOCKED: {
    title: 'Blocked start trial events attributed to your selected filters',
  },
  OPT_IN: {
    title: 'Event is triggered when the user opts in to tracking via ATT',
  },
  OPT_OUT: {
    title: 'Event is triggered when the user opts out of tracking via ATT',
  },
  attributed: {
    title:
      'Whether the event was attributed to any touch (click or impression) as opposed to unattributed (attributed: false). Unattributed data is the same as "organic" or "direct" data.',
  },
  user_data_platform: {
    title: 'Platform (web vs app, desktop vs mobile) on which the event took place',
  },
  user_data_environment: {
    title: 'Environment where the event occurred, which can distinguish between e.g. full app vs instant app',
  },
  user_data_geo_country_code: {
    title: 'Country, derived from IP address',
  },
  user_data_os: {
    title: 'Operating system',
  },
  last_attributed_touch_data_tilde_advertising_partner_name: {
    title: 'Ad partner of last touch – this is set automatically to be the name of the ad partner',
  },
  last_attributed_touch_data_dollar_3p: {
    title: 'Ad partner (3p) of last touch – this is set automatically to be the 3p value of the ad partner',
  },
  last_attributed_touch_data_tilde_secondary_publisher: {
    title:
      'Secondary publisher of last touch – this is set automatically to include information about the publisher where the ad was served',
  },
  last_attributed_touch_data_tilde_ad_set_name: {
    title: 'Ad set name of last touch – this is set automatically by ad networks like Facebook',
  },
  last_attributed_touch_data_tilde_ad_name: {
    title: 'Ad name of last touch – this is set automatically by ad networks like Facebook',
  },
  last_attributed_touch_data_tilde_tune_publisher_id: {
    title:
      'Tune Publisher ID of the last touch – this is set automatically to include information about the publisher ID where the ad was served',
  },
  last_attributed_touch_data_dollar_marketing_title: {
    title: 'Marketing title of the last attributed touch (Quick Links only)',
  },
  last_attributed_touch_data_tilde_content_id: {
    title:
      'Content ID of the last touch - this can be set as a custom analytics tag, or set automatically by ad networks like Facebook',
  },
  last_attributed_touch_data_tilde_creative_name: {
    title: 'Creative name of last touch – this is set automatically by ad networks like Facebook',
  },
  last_attributed_touch_data_tilde_type: {
    title: 'Whether the last touch was an impression, click',
  },
  last_attributed_touch_data_tilde_keyword: {
    title: 'Keyword of last touch',
  },
  fraud_names: {
    title: 'The name of the fraud rule responsible for blocking an event',
  },
  fraud_codes: {
    title: 'The code (ID) of the fraud rule responsible for blocking an event',
  },
  name: {
    title: 'Event name',
  },
  timestamp: {
    title: 'Event timestamp',
  },
  origin: {
    title: 'Whether the event originated with a Branch SDK/API call, or was sent by Segment or mParticle',
  },
  last_attributed_touch_type: {
    title: 'Whether the last touch was an impression, click',
  },
  last_attributed_touch_timestamp: {
    title: 'Unix timestamp in milliseconds for the last attributed touch',
  },
  deep_linked: {
    title: 'True if the current app or web session resulted in the user being deep linked',
  },
  user_data_aaid: {
    title: 'Android/Google advertising id of the device where the event occurred',
  },
  user_data_idfa: {
    title: 'iOS identifier for advertising – shared across all apps',
  },
  user_data_idfv: {
    title: 'iOS identifier for vendor – scoped to vendor',
  },
  user_data_android_id: {
    title: 'Android hardware id',
  },
  id: {
    title: 'Event id',
  },
  last_attributed_touch_data_tilde_id: {
    title: 'id for the last attributed touch (Quick Links only)',
  },
  last_attributed_touch_data_tilde_campaign_id: {
    title:
      'Campaign id of last touch – this is set automatically by ad networks like Facebook, or can be set with analytics tags on a link',
  },
  last_attributed_touch_data_tilde_creative_id: {
    title: 'Creative id of the last touch – this is set automatically by ad networks like Facebook',
  },
  last_attributed_touch_data_tilde_ad_set_id: {
    title: 'Ad set id of last touch – this is set automatically by ad networks like Facebook',
  },
  last_attributed_touch_data_tilde_ad_id: {
    title: 'Ad id of last touch – this is set automatically by ad networks like Facebook',
  },
  last_attributed_touch_data_tilde_branch_ad_format: {
    title:
      'Ad format of the last touch – chosen upon Branch link creation and set as an analytics tag (e.g. Search, Display, Product Ad, App only)',
  },
  last_attributed_touch_data_tilde_technology_partner: {
    title:
      'Technology partner of last touch, as set with an analytics tag – any third party that is used to enhance a campaign (e.g. audiences being provided, creative optimization), with the exception of agencies',
  },
  last_attributed_touch_data_tilde_banner_dimensions: {
    title: 'Dimensions of display banner of the last touch, as set with an analytics tag',
  },
  last_attributed_touch_data_tilde_placement: {
    title:
      'Placement of the last touch, as set with an analytics tag – actual app or website the ad appears on display campaigns',
  },
  last_attributed_touch_data_tilde_keyword_id: {
    title: 'Unique id for keyword of the last touch as provided by Google',
  },
  last_attributed_touch_data_tilde_optimization_model: {
    title:
      'Optimization model of the last touch, as set with an analytics tag – model detailing the event for which the campaign is optimizing (e.g. for installs, clicks, events)',
  },
  last_attributed_touch_data_tilde_secondary_ad_format: {
    title:
      'Secondary ad format of the last touch, as set with an analytics tag or during ad link creation flow in the dashboard – Ad Partner Specific Ad format, such as Display Engagement Ad for Google',
  },
  referring_domain: {
    title:
      'Domain of the URL that the user visited before landing on a page with a Journeys – this should use the same data source that we use for the "came directly from a URL" audience filter',
  },
  current_feature: {
    title: 'Branch feature (determined by Branch) of the current touch',
  },
  via_features: {
    title: 'All Branch features (determined by Branch) which were determined, via attribution, to have led to this event',
  },
  third_party_provider: {
    title: 'Advertising partner short-code (e.g. e_rs) specified for the last attributed touch',
  },
  web_format: {
    title: 'Set if an event originated with a touch on Google AMP or Facebook Instant Article',
  },
  last_attributed_touch_data: {
    title:
      'For touches, this includes all touch data, including analytics tags, redirection information, and custom key-value pairs – for all other events, this includes the same data, but from the touch to which this event is attributed',
  },
  user_data_os_version: {
    title: 'Operating system version of the device where the event occurred',
  },
  user_data_limit_ad_tracking: {
    title: 'Whether limit ad tracking was set to true to the event',
  },
  user_data_user_agent: {
    title: 'User agent of the browser or app where the event occurred – usually associated with a webview',
  },
  user_data_ip: {
    title: 'IP address',
  },
  user_data_developer_identity: {
    title: 'Developer-specified identity for a user',
  },
  user_data_language: {
    title: 'Language specified in the user_agent string (or in some cases other headers)',
  },
  user_data_brand: {
    title: 'Brand of the device where the event occurred',
  },
  user_data_device_type: {
    title: 'Device type of the device where the event occurred',
  },
  user_data_model: {
    title: 'Model of the device where the event occurred',
  },
  user_data_sdk_version: {
    title: 'Branch SDK version used to track this event',
  },
  user_data_app_version: {
    title: 'App version where the event occurred',
  },
  user_data_geo_dma_code: {
    title: 'DMA code, derived from IP address',
  },
  transaction_id: {
    title: 'Partner-specified transaction id for their internal use',
  },
  revenue_in_usd: {
    title: 'Revenue in USD, based off of revenue, currency, and exchange rate',
  },
  exchange_rate: {
    title:
      'Exchange rate from the time the event occurred (may be up to 24 hours old but is typically updated within the hour)',
  },
  currency: {
    title: 'Currency that revenue, price, shipping, tax were orginally reported in by the partner',
  },
  shipping: {
    title: 'Shipping cost associated with the transaction',
  },
  tax: {
    title: 'Total tax associated with the transaction',
  },
  coupon: {
    title: 'Transaction coupon redeemed with the transaction (e.g. "SPRING2017")',
  },
  affiliation: {
    title: 'Store or affiliation from which this transaction occurred (e.g. Google Store)',
  },
  search_query: {
    title: 'Search query associated with the event',
  },
  event_description: {
    title: 'Description associated with the event, not necessarily specific to any individual content items',
  },
  custom_data: {
    title: 'Custom data associated with the event',
  },
  content_items: {
    title: 'Content items associated with the event',
  },
  dismissal_source: {
    title: 'Journey dismissal source',
  },
  event_data_custom_param_1: {
    title: 'Custom parameter',
  },
  user_data_app_store: {
    title: 'App store on event',
  },
  event_data_local_currency: {
    title: 'Local Currency value set on the Dashboard Configuration section in the time-frame.',
  },
  impression_conversion_rate: {
    title: 'The impressions it takes, on average, for a user to install your app - (Installs/Impressions)x100',
  },
};

export const REPORT_COLUMN_KEYS: { [key: string]: string } = {
  eo_click: DATA_FIELD_KEYS.CLICK,
  eo_install: DATA_FIELD_KEYS.INSTALL,
  eo_reinstall: DATA_FIELD_KEYS.REINSTALL,
  eo_open: DATA_FIELD_KEYS.OPEN,
  eo_web_session_start: DATA_FIELD_KEYS.WEB_SESSION_START,
  eo_branch_cta_view: DATA_FIELD_KEYS.BRANCH_CTA_VIEW,
  eo_sms_sent: DATA_FIELD_KEYS.SMS_SENT,
  eo_web_to_app_auto_redirect: DATA_FIELD_KEYS.WEB_TO_APP_AUTO_REDIRECT,
  eo_impression: DATA_FIELD_KEYS.IMPRESSION,
  eo_pageview: DATA_FIELD_KEYS.PAGEVIEW,
  eo_dismissal: DATA_FIELD_KEYS.DISMISSAL,
  cost: DATA_FIELD_KEYS.COST,
  cost_in_app_local_currency: DATA_FIELD_KEYS.COST_IN_LOCAL_CURRENCY,

  calculated_eCPI: DATA_FIELD_KEYS.ECPI,
  calculated_eCPI_in_local_currency: DATA_FIELD_KEYS.ECPI_IN_LOCAL_CURRENCY,
  calculated_eCPC: DATA_FIELD_KEYS.ECPC,
  calculated_eCPM: DATA_FIELD_KEYS.ECPM,
  calculated_eCPM_in_local_currency: DATA_FIELD_KEYS.ECPM_IN_LOCAL_CURRENCY,
  calculated_eCPA_in_local_currency: DATA_FIELD_KEYS.ECPA_IN_LOCAL_CURRENCY,
  calculated_eCPA: DATA_FIELD_KEYS.ECPA,
  calculated_eCPC_in_local_currency: DATA_FIELD_KEYS.ECPC_IN_LOCAL_CURRENCY,
  calculated_ROI: DATA_FIELD_KEYS.ROI,
  calculated_ROAS: DATA_FIELD_KEYS.ROAS,
  eo_click_to_eo_install: DATA_FIELD_KEYS.CLICK_TO_INSTALL,
  eo_click_to_eo_reinstall: DATA_FIELD_KEYS.CLICK_TO_REINSTALL,
  eo_click_to_eo_open: DATA_FIELD_KEYS.CLICK_TO_OPEN,
  eo_click_to_eo_web_session_start: DATA_FIELD_KEYS.CLICK_TO_WEB_SESSION_START,
  impression_conversion_rate: 'impression_conversion_rate',

  [CALCULATION_KEY.EO_BRANCH_CTA_VIEW_TO_EO_CLICK]: DATA_FIELD_KEYS.VIEW_TO_CLICK,
  [CALCULATION_KEY.EO_BRANCH_CTA_VIEW_TO_EO_INSTALL]: DATA_FIELD_KEYS.VIEW_TO_INSTALL,
  [CALCULATION_KEY.EO_BRANCH_CTA_VIEW_TO_EO_OPEN]: DATA_FIELD_KEYS.VIEW_TO_OPEN,
  [CALCULATION_KEY.EO_BRANCH_CTA_VIEW_TO_EO_DISMISSAL]: DATA_FIELD_KEYS.VIEW_TO_DISMISSAL,
  [CALCULATION_KEY.EO_BRANCH_CTA_VIEW_TO_PURCHASE]: DATA_FIELD_KEYS.VIEW_TO_PURCHASE,
  [CALCULATION_KEY.EO_BRANCH_CTA_VIEW_TO_ADD_TO_CART]: DATA_FIELD_KEYS.VIEW_TO_ADD_TO_CART,
  [CALCULATION_KEY.EO_BRANCH_CTA_VIEW_TO_ADD_TO_WISHLIST]: DATA_FIELD_KEYS.VIEW_TO_ADD_TO_WISHLIST,
  [CALCULATION_KEY.EO_BRANCH_CTA_VIEW_TO_VIEW_CART]: DATA_FIELD_KEYS.VIEW_TO_VIEW_CART,
  [CALCULATION_KEY.EO_BRANCH_CTA_VIEW_TO_INITIATE_PURCHASE]: DATA_FIELD_KEYS.VIEW_TO_INITIATE_PURCHASE,
  [CALCULATION_KEY.EO_BRANCH_CTA_VIEW_TO_ADD_PAYMENT_INFO]: DATA_FIELD_KEYS.VIEW_TO_ADD_PAYMENT_INFO,
  [CALCULATION_KEY.EO_BRANCH_CTA_VIEW_TO_SPEND_CREDITS]: DATA_FIELD_KEYS.VIEW_TO_SPEND_CREDITS,
  [CALCULATION_KEY.EO_BRANCH_CTA_VIEW_TO_RESERVE]: DATA_FIELD_KEYS.VIEW_TO_RESERVE,
  [CALCULATION_KEY.EO_BRANCH_CTA_VIEW_TO_CLICK_AD]: DATA_FIELD_KEYS.VIEW_TO_CLICK_AD,
  [CALCULATION_KEY.EO_BRANCH_CTA_VIEW_TO_VIEW_AD]: DATA_FIELD_KEYS.VIEW_TO_VIEW_AD,

  PURCHASE: DATA_FIELD_KEYS.PURCHASE,
  revenue: DATA_FIELD_KEYS.REVENUE,
  revenue_in_local_currency: DATA_FIELD_KEYS.REVENUE_IN_LOCAL_CURRENCY,
  ADD_TO_CART: DATA_FIELD_KEYS.ADD_TO_CART,
  ADD_TO_WISHLIST: DATA_FIELD_KEYS.ADD_TO_WISHLIST,
  VIEW_CART: DATA_FIELD_KEYS.VIEW_CART,
  INITIATE_PURCHASE: DATA_FIELD_KEYS.INITIATE_PURCHASE,
  ADD_PAYMENT_INFO: DATA_FIELD_KEYS.ADD_PAYMENT_INFO,
  SPEND_CREDITS: DATA_FIELD_KEYS.SPEND_CREDITS,
  RESERVE: DATA_FIELD_KEYS.RESERVE,
  CLICK_AD: DATA_FIELD_KEYS.CLICK_AD,
  VIEW_AD: DATA_FIELD_KEYS.VIEW_AD,

  SEARCH: DATA_FIELD_KEYS.SEARCH,
  VIEW_ITEM: DATA_FIELD_KEYS.VIEW_ITEM,
  VIEW_ITEMS: DATA_FIELD_KEYS.VIEW_ITEMS,
  RATE: DATA_FIELD_KEYS.RATE,
  SHARE: DATA_FIELD_KEYS.SHARE,
  INITIATE_STREAM: DATA_FIELD_KEYS.INITIATE_STREAM,
  COMPLETE_STREAM: DATA_FIELD_KEYS.COMPLETE_STREAM,

  COMPLETE_REGISTRATION: DATA_FIELD_KEYS.COMPLETE_REGISTRATION,
  COMPLETE_TUTORIAL: DATA_FIELD_KEYS.COMPLETE_TUTORIAL,
  ACHIEVE_LEVEL: DATA_FIELD_KEYS.ACHIEVE_LEVEL,
  UNLOCK_ACHIEVEMENT: DATA_FIELD_KEYS.UNLOCK_ACHIEVEMENT,
  INVITE: DATA_FIELD_KEYS.INVITE,
  LOGIN: DATA_FIELD_KEYS.LOGIN,
  SUBSCRIBE: DATA_FIELD_KEYS.SUBSCRIBE,
  START_TRIAL: DATA_FIELD_KEYS.START_TRIAL,
  OPT_IN: DATA_FIELD_KEYS.OPT_IN,
  OPT_OUT: DATA_FIELD_KEYS.OPT_OUT,
};

export const TIME_SERIES_CALCULATION_TOPICS: { [key: string]: string } = {
  [ECPI]: DATA_FIELD_KEYS.ECPI,
  [ECPC]: DATA_FIELD_KEYS.ECPC,
  [ECPM]: DATA_FIELD_KEYS.ECPM,
  [ECPA]: DATA_FIELD_KEYS.ECPA,
  [ROI]: DATA_FIELD_KEYS.ROI,
  [ROAS]: DATA_FIELD_KEYS.ROAS,
  [ECPA_IN_LOCAL_CURRENCY]: DATA_FIELD_KEYS.ECPA_IN_LOCAL_CURRENCY,
  [ECPC_IN_LOCAL_CURRENCY]: DATA_FIELD_KEYS.ECPC_IN_LOCAL_CURRENCY,
  [ECPI_IN_LOCAL_CURRENCY]: DATA_FIELD_KEYS.ECPI_IN_LOCAL_CURRENCY,
  [ECPM_IN_LOCAL_CURRENCY]: DATA_FIELD_KEYS.ECPM_IN_LOCAL_CURRENCY,
};

export const GROUPS: { [groupName: string]: Group } = {
  eo_install_seconds_from_last_attributed_touch_to_event_under_10: {
    key: 'short_cti_times',
  },
  eo_install_seconds_from_last_attributed_touch_to_event_between_10_and_30: {
    key: 'short_cti_times',
  },
  eo_install_seconds_from_last_attributed_touch_to_event_over_30: {
    key: 'short_cti_times',
  },
  eo_install_days_from_last_attributed_touch_to_event_under_1: {
    key: 'long_cti_times',
  },
  eo_install_days_from_last_attributed_touch_to_event_over_1: {
    key: 'long_cti_times',
  },
  eo_install_blocked: {
    key: 'blocked_install_group',
  },
  eo_install_blocked_to_eo_install_total: {
    key: 'blocked_install_group',
  },
  eo_click_blocked: {
    key: 'blocked_click_group',
  },
  eo_click_blocked_to_eo_click_total: {
    key: 'blocked_click_group',
  },
  eo_open_blocked: {
    key: 'blocked_open_group',
  },
  eo_open_blocked_to_eo_open_total: {
    key: 'blocked_open_group',
  },
  eo_reinstall_blocked: {
    key: 'blocked_reinstall_group',
  },
  eo_reinstall_blocked_to_eo_reinstall_total: {
    key: 'blocked_reinstall_group',
  },
  eo_impression_blocked: {
    key: 'blocked_impression_group',
  },
  eo_impression_blocked_to_eo_impression_total: {
    key: 'blocked_impression_group',
  },
  fraud_codes: {
    key: 'fraud_codes_group',
  },
  fraud_codes_description: {
    key: 'fraud_codes_group',
  },
};

export const COLUMN_HEADER_STYLE: { [key: string]: string } = {
  eo_install_blocked: 'h_jc-c',
  eo_install_blocked_to_eo_install_total: 'h_jc-c',
  eo_click_blocked: 'h_jc-c',
  eo_click_blocked_to_eo_click_total: 'h_jc-c',
  eo_open_blocked: 'h_jc-c',
  eo_open_blocked_to_eo_open_total: 'h_jc-c',
  eo_reinstall_blocked: 'h_jc-c',
  eo_reinstall_blocked_to_eo_reinstall_total: 'h_jc-c',
  eo_impression_blocked: 'h_jc-c',
  eo_impression_blocked_to_eo_impression_total: 'h_jc-c',
};

export const GRADIENT_COLUMNS: string[] = [
  'eo_install_seconds_from_last_attributed_touch_to_event_under_10',
  'eo_install_days_from_last_attributed_touch_to_event_over_1',
  'eo_install_blocked_to_eo_install_total',
  'eo_click_blocked_to_eo_click_total',
  'eo_open_blocked_to_eo_open_total',
  'eo_reinstall_blocked_to_eo_reinstall_total',
  'eo_impression_blocked_to_eo_impression_total',
];

export const EVENT_TYPE_DATA_SOURCES = 'data_sources' as const;
export const EVENT_TYPE_STANDARD_EVENT = 'standard_event' as const;
export const EVENT_TYPE_EO_CUSTOM_EVENT = 'eo_custom_event' as const;
export const EVENT_TYPE_EO_CUSTOM_EVENT_BLOCKED = 'eo_custom_event_blocked' as const;
export const EVENT_TYPE_FORMULA = 'formula' as const;
export const EVENT_TYPE_CURRENCY_FORMULA = 'currency_formula' as const;
export const EVENT_TYPE_DIMENSION = 'dimension' as const;
export const EVENT_TYPE_SKAN = 'skan' as const;
export const EVENT_TYPE_CUSTOM_CALCULATION = 'custom_calculation' as const;

export const COLUMN_TYPE = {
  DATASOURCE: EVENT_TYPE_DATA_SOURCES,
  STANDARD_EVENT: EVENT_TYPE_STANDARD_EVENT,
  CUSTOM_EVENT: EVENT_TYPE_EO_CUSTOM_EVENT,
  CUSTOM_EVENT_BLOCKED: EVENT_TYPE_EO_CUSTOM_EVENT_BLOCKED,
  FORMULA: EVENT_TYPE_FORMULA,
  CURRENCY_FORMULA: EVENT_TYPE_CURRENCY_FORMULA,
  DIMENSION: EVENT_TYPE_DIMENSION,
  SKAN: EVENT_TYPE_SKAN,
  CUSTOM_CALCULATION: EVENT_TYPE_CUSTOM_CALCULATION,
};
